import { Button, Typography, Row, Col } from 'antd';
import { motion } from 'framer-motion';
import {
    PlayCircleFilled,
    UnorderedListOutlined,
    CalendarOutlined,
} from '@ant-design/icons';
import { FaTrophy } from 'react-icons/fa';
import { IAwardWithWinner } from '../../../Steps/ImportData/useImportData';
import { awardImagesUrl, urlHttpGov } from '../../../../../../services/urls';
import { formatDateStringToDateAndTime } from '../../../../../../utils/Conversores';
import ImagemLogo from '../../../../../../assets/images/logo-lateral.png';
import useAuth from '../../../../../../hooks/useAuth';

const { Title, Text } = Typography;

interface DrawIntroProps {
    giveawayTitle?: string;
    giveawayDate?: string | Date;
    cityName?: string;
    attemptNumber?: number;
    awards: IAwardWithWinner[];
    onStartDraw: () => void;
    onSkipToSummary: () => void;
}

export default function DrawIntro({
    giveawayTitle,
    giveawayDate,
    cityName,
    awards,
    onStartDraw,
    onSkipToSummary,
}: DrawIntroProps) {
    const totalPrizes = awards.length;
    const { activeCity } = useAuth();

    const cityLogoUrl = activeCity?.logo_county
        ? `${urlHttpGov}/logo_county/${activeCity.logo_county}`
        : activeCity?.logo_light
            ? `${urlHttpGov}/logo_county/${activeCity.logo_light}`
            : null;

    return (
        <div
            style={{
                maxWidth: 920,
                width: '100%',
                margin: '0 auto',
                padding: '36px 20px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 28,
            }}
        >
            {/* Logos Centered */}
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.35 }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 24,
                    flexWrap: 'wrap',
                }}
            >
                <img
                    src={ImagemLogo}
                    alt="Programa Nota Município"
                    style={{
                        height: 60,
                        maxWidth: 240,
                        objectFit: 'contain',
                    }}
                />

                {cityLogoUrl && (
                    <>
                        <div style={{ height: 40, width: 1, background: '#e2e8f0' }} />
                        <img
                            src={cityLogoUrl}
                            alt={cityName || 'Prefeitura'}
                            style={{
                                height: 60,
                                maxWidth: 200,
                                objectFit: 'contain',
                            }}
                        />
                    </>
                )}
            </motion.div>

            {/* Title & Slogan */}
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.35 }}
                style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}
            >
                <Title
                    level={2}
                    style={{
                        color: '#0f172a',
                        margin: 0,
                        fontWeight: 800,
                        fontSize: 26,
                    }}
                >
                    {giveawayTitle || 'Apuração Oficial do Sorteio'}
                </Title>

                {giveawayDate && (
                    <Text style={{ color: '#64748b', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <CalendarOutlined />
                        Data do Sorteio: {formatDateStringToDateAndTime(String(giveawayDate))}
                    </Text>
                )}
            </motion.div>

            {/* Prizes Grid */}
            <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.35 }}
                style={{ width: '100%' }}
            >
                <div
                    className="municipal-card"
                    style={{
                        padding: '20px 24px',
                        textAlign: 'left',
                        background: '#ffffff',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: 16,
                            flexWrap: 'wrap',
                            gap: 8,
                        }}
                    >
                        <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <FaTrophy color="#f59e0b" />
                            Prêmios da Apuração ({totalPrizes})
                        </span>
                        <span style={{ fontSize: 13, color: '#64748b' }}>
                            Ordem: 1º ao {totalPrizes}º Prêmio
                        </span>
                    </div>

                    <Row gutter={[12, 12]}>
                        {awards.slice(0, 6).map((award, index) => {
                            const rankNumber = index + 1;
                            return (
                                <Col key={award.id || index} xs={24} sm={12} md={8}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            background: '#f8fafc',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 10,
                                            padding: '10px 12px',
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: 42,
                                                height: 42,
                                                borderRadius: 8,
                                                background: '#ffffff',
                                                border: '1px solid #e2e8f0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {award.images && award.images.length > 0 ? (
                                                <img
                                                    src={`${awardImagesUrl}/${award.images[0].img}`}
                                                    alt={award.description}
                                                    style={{ width: '90%', height: '90%', objectFit: 'contain' }}
                                                />
                                            ) : (
                                                <FaTrophy color="#f59e0b" size={16} />
                                            )}
                                        </div>

                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706' }}>
                                                {rankNumber}º Prêmio
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: '#1e293b',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    marginTop: 2,
                                                }}
                                            >
                                                {award.description}
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                    {totalPrizes > 6 && (
                        <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                            + outros {totalPrizes - 6} prêmios nesta apuração
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Action CTAs */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.35 }}
                style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}
            >
                <Button
                    type="primary"
                    size="large"
                    icon={<PlayCircleFilled />}
                    onClick={onStartDraw}
                    style={{
                        height: 48,
                        padding: '0 32px',
                        fontSize: 15,
                        fontWeight: 700,
                        borderRadius: 10,
                        background: '#2563eb',
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    Iniciar Apuração
                </Button>

                <Button
                    type="default"
                    size="large"
                    icon={<UnorderedListOutlined />}
                    onClick={onSkipToSummary}
                    style={{
                        height: 48,
                        padding: '0 24px',
                        fontSize: 14,
                        fontWeight: 600,
                        borderRadius: 10,
                        background: '#ffffff',
                        borderColor: '#cbd5e1',
                        color: '#334155',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    Ver Lista de Ganhadores
                </Button>
            </motion.div>
        </div>
    );
}
